import type { UseFormReturn } from "react-hook-form";
import { useFieldArray, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CreatableSelect from "react-select/creatable";
import type { IntegratorFormData } from "../hooks/useIntegratorForm";
import type { ThemeClasses } from "../types";
import {
  ADAPTER_OPTIONS,
  AUTH_STRATEGY_OPTIONS,
  PROTOCOL_OPTIONS,
} from "../types";
import { getCustomSelectStyles } from "../../../../../admin/shared";

interface IntegratorFormModalProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  form: UseFormReturn<IntegratorFormData>;
  loadingSubmit: boolean;
  onClose: () => void;
  onSubmit: (data: IntegratorFormData) => Promise<void>;
  isEdit: boolean;
  hasFormChanges: () => boolean;
}

export default function IntegratorFormModal({
  themeClasses,
  isDarkMode,
  form,
  loadingSubmit,
  onClose,
  onSubmit,
  isEdit,
  hasFormChanges,
}: IntegratorFormModalProps) {
  const { t } = useTranslation("resellerAdmin");
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "credentials_schema",
  });

  const active = watch("active");

  const inputClass = `w-full px-3 py-2.5 rounded-xl border ${themeClasses.border} ${themeClasses.bg} ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50`;
  const labelClass = `block text-sm font-medium ${themeClasses.text} mb-1`;

  const selectStyles = getCustomSelectStyles(isDarkMode, false);
  const adapterOptions = ADAPTER_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(`integrators.${opt.labelKey}`),
  }));
  const authStrategyOptions = AUTH_STRATEGY_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(`integrators.${opt.labelKey}`),
  }));
  const protocolOptions = PROTOCOL_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(`integrators.${opt.labelKey}`),
  }));

  // Permite mostrar un valor escrito manualmente aunque no esté en las opciones.
  type Opt = { value: string; label: string };
  const valueOption = (options: Opt[], val: string): Opt | null =>
    val ? options.find((o) => o.value === val) || { value: val, label: val } : null;
  const formatCreateLabel = (input: string) =>
    t("integrators.createOption", { value: input });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div
        className={`relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl ${themeClasses.bg} shadow-2xl`}
      >
        {/* Header */}
        <div
          className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${themeClasses.border} ${themeClasses.bg}`}
        >
          <h2 className={`text-lg font-bold ${themeClasses.text}`}>
            <i className="ri-plug-line mr-2"></i>
            {isEdit
              ? t("integrators.modal.editTitle")
              : t("integrators.modal.addTitle")}
          </h2>
          <button
            aria-label={t("general.close", { ns: "common" })}
            onClick={onClose}
            className={`p-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText}`}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* === BASIC INFO === */}
          <div>
            <h3
              className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3 flex items-center gap-1`}
            >
              <i className="ri-information-line"></i>{" "}
              {t("integrators.sections.basicInfo")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
              {/* Name */}
              <div className="sm:col-span-2">
                <label className={labelClass}>
                  {t("integrators.fields.name")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("name", {
                    required: t("integrators.validation.nameRequired"),
                  })}
                  className={inputClass}
                  placeholder={t("integrators.placeholders.name")}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Estado (envía `active` por debajo) */}
              <div>
                <label className={labelClass}>
                  {t("integrators.fields.status")}
                </label>
                <button
                  type="button"
                  onClick={() =>
                    setValue("active", !active, { shouldDirty: true })
                  }
                  className={`w-full flex items-center justify-center gap-2 px-3 h-[42px] rounded-xl border text-sm transition-all ${
                    active
                      ? "bg-green-100 border-green-400 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.textSecondary}`
                  }`}
                >
                  <i
                    className={`${
                      active ? "ri-toggle-fill" : "ri-toggle-line"
                    } text-base`}
                  ></i>
                  {active
                    ? t("integrators.active")
                    : t("integrators.inactive")}
                </button>
              </div>
            </div>
          </div>

          {/* === CONNECTION === */}
          <div className={`p-4 rounded-xl ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
            <h3
              className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3 flex items-center gap-1`}
            >
              <i className="ri-links-line"></i>{" "}
              {t("integrators.sections.connection")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Adapter */}
              <div>
                <label className={labelClass}>
                  {t("integrators.fields.adapter")}
                </label>
                <Controller
                  name="adapter"
                  control={control}
                  render={({ field }) => (
                    <CreatableSelect
                      options={adapterOptions}
                      styles={selectStyles}
                      placeholder={t("integrators.placeholders.selectAdapter")}
                      value={valueOption(adapterOptions, field.value)}
                      onChange={(option: any) =>
                        field.onChange(option?.value || "")
                      }
                      formatCreateLabel={formatCreateLabel}
                      isSearchable
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                  {t("integrators.manualHint")}
                </p>
              </div>

              {/* Auth strategy */}
              <div>
                <label className={labelClass}>
                  {t("integrators.fields.authStrategy")}
                </label>
                <Controller
                  name="auth_strategy"
                  control={control}
                  render={({ field }) => (
                    <CreatableSelect
                      options={authStrategyOptions}
                      styles={selectStyles}
                      placeholder={t(
                        "integrators.placeholders.selectAuthStrategy"
                      )}
                      value={valueOption(authStrategyOptions, field.value)}
                      onChange={(option: any) =>
                        field.onChange(option?.value || "")
                      }
                      formatCreateLabel={formatCreateLabel}
                      isSearchable
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                  {t("integrators.manualHint")}
                </p>
              </div>

              {/* Protocol */}
              <div>
                <label className={labelClass}>
                  {t("integrators.fields.protocol")}
                </label>
                <Controller
                  name="protocol"
                  control={control}
                  render={({ field }) => (
                    <CreatableSelect
                      options={protocolOptions}
                      styles={selectStyles}
                      placeholder={t("integrators.placeholders.selectProtocol")}
                      value={valueOption(protocolOptions, field.value)}
                      onChange={(option: any) =>
                        field.onChange(option?.value || "")
                      }
                      formatCreateLabel={formatCreateLabel}
                      isSearchable
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                  {t("integrators.manualHint")}
                </p>
              </div>

              {/* Default endpoint */}
              <div>
                <label className={labelClass}>
                  {t("integrators.fields.defaultEndpoint")}
                </label>
                <input
                  type="url"
                  {...register("default_endpoint", {
                    pattern: {
                      value: /^https?:\/\/.+/i,
                      message: t("integrators.validation.endpointInvalid"),
                    },
                  })}
                  className={inputClass}
                  placeholder={t("integrators.placeholders.defaultEndpoint")}
                />
                {errors.default_endpoint && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.default_endpoint.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* === CREDENTIALS SCHEMA === */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3
                className={`text-sm font-semibold ${themeClasses.textSecondary} flex items-center gap-1`}
              >
                <i className="ri-key-2-line"></i>{" "}
                {t("integrators.sections.credentials")}
              </h3>
              <button
                type="button"
                onClick={() => append({ field: "", label: "", secret: false })}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500/10 text-primary-600 dark:text-primary-400 hover:bg-primary-500/20 transition-all text-sm font-medium"
              >
                <i className="ri-add-line"></i>
                {t("integrators.actions.addCredential")}
              </button>
            </div>

            {fields.length === 0 && (
              <p className={`text-sm ${themeClasses.textSecondary} text-center py-4`}>
                {t("integrators.noCredentials")}
              </p>
            )}

            <div className="space-y-2">
              {fields.map((fieldItem, index) => (
                <div
                  key={fieldItem.id}
                  className={`flex items-end gap-3 p-3 rounded-xl ${
                    isDarkMode ? "bg-dark-bg" : "bg-gray-50"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <label className={`block text-xs ${themeClasses.textSecondary} mb-1`}>
                      {t("integrators.fields.credentialField")}
                    </label>
                    <input
                      {...register(`credentials_schema.${index}.field` as const)}
                      className={inputClass}
                      placeholder={t("integrators.placeholders.credentialField")}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className={`block text-xs ${themeClasses.textSecondary} mb-1`}>
                      {t("integrators.fields.credentialLabel")}
                    </label>
                    <input
                      {...register(`credentials_schema.${index}.label` as const)}
                      className={inputClass}
                      placeholder={t("integrators.placeholders.credentialLabel")}
                    />
                  </div>
                  <label
                    className={`flex items-center gap-2 h-[42px] shrink-0 whitespace-nowrap text-xs ${themeClasses.text} cursor-pointer`}
                  >
                    <input
                      type="checkbox"
                      {...register(
                        `credentials_schema.${index}.secret` as const
                      )}
                      className="w-4 h-4 rounded accent-primary-500"
                    />
                    {t("integrators.fields.credentialSecret")}
                  </label>
                  <div className={`shrink-0 pl-3 ml-1 border-l ${themeClasses.border}`}>
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="flex items-center justify-center h-[42px] w-[42px] rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-all"
                    >
                      <i className="ri-delete-bin-line text-lg"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* === SUBMIT === */}
          <div
            className={`sticky bottom-0 flex items-center justify-end gap-3 pt-4 border-t ${themeClasses.border} ${themeClasses.bg}`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium ${themeClasses.buttonText} ${themeClasses.buttonHover} transition-all`}
            >
              {t("integrators.actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={loadingSubmit || (isEdit && !hasFormChanges())}
              className="px-6 py-2.5 rounded-xl text-sm font-medium bg-primary-500 text-gray-900 hover:bg-primary-600 transition-all shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loadingSubmit && (
                <div className="w-4 h-4 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin"></div>
              )}
              {isEdit
                ? t("integrators.actions.saveChanges")
                : t("integrators.actions.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
